import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/apiClient';
import { jobNumber, jobName } from '../utils/ui';

const BUCKETS = ['NVE / Power Company', 'Horizontal Drilling', 'Vertical Drilling', 'Shop / Trucking', 'General', 'Loops'];

// Color per workflow bucket so a mis-file jumps out visually.
const TAG_COLOR = {
  'NVE / Power Company': { bg: '#ede9fe', color: '#5b21b6' },
  'Horizontal Drilling': { bg: '#ffedd5', color: '#9a3412' },
  'Vertical Drilling':   { bg: '#fef3c7', color: '#92400e' },
  'Shop / Trucking':     { bg: '#dbeafe', color: '#1e40af' },
  'General':             { bg: '#f3f4f6', color: '#374151' },
  'Loops':               { bg: '#dcfce7', color: '#166534' },
};

function fmtWhen(d) {
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  return dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function MyUpdates() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);   // note being re-tagged
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try { setRows(await apiFetch('/my-updates')); } catch { setRows([]); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function retag(id, bucket) {
    setSaving(true);
    try {
      await apiFetch(`/daily-notes/${id}/tags`, { method: 'PATCH', body: JSON.stringify({ subJobs: [bucket] }) });
      setRows(prev => prev.map(r => r.id === id ? { ...r, parsed_sub_jobs: [bucket] } : r));
      setEditId(null);
    } catch {}
    setSaving(false);
  }

  return (
    <div className="page" style={{ maxWidth: 760, margin: '0 auto', padding: '0 0 40px' }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 16px 10px', position: 'sticky', top: 0, background: '#0f2747', zIndex: 5 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: 4 }}>‹</button>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>My Updates</div>
          <div style={{ fontSize: 12, color: '#9fb3d1' }}>Every note you've posted · newest first</div>
        </div>
      </div>

      <div style={{ padding: '14px 14px 0' }}>
        {loading && <div style={{ color: '#6b7280', padding: 20, textAlign: 'center' }}>Loading…</div>}
        {!loading && rows.length === 0 && (
          <div style={{ color: '#6b7280', padding: 30, textAlign: 'center', background: '#fff', border: '1.5px solid #ececec', borderRadius: 12 }}>
            You haven't posted any job updates yet.
          </div>
        )}

        {rows.map(r => {
          const tags = Array.isArray(r.parsed_sub_jobs) ? r.parsed_sub_jobs : [];
          const editing = editId === r.id;
          return (
            <div key={r.id} style={{ background: '#fff', border: '1.5px solid #ececec', borderRadius: 12, padding: 14, marginBottom: 12 }}>
              {/* Job + date */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                <button
                  onClick={() => r.project_id && navigate(`/sup/project/${r.project_id}`)}
                  style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: r.project_id ? 'pointer' : 'default', color: '#0f2747', fontWeight: 800, fontSize: 14 }}>
                  {r.project_name
                    ? <>{jobNumber({ project_number: r.project_number, name: r.project_name }) && <span style={{ color: '#e87722' }}>{jobNumber({ project_number: r.project_number, name: r.project_name })} · </span>}{jobName(r.project_name)}</>
                    : 'Unassigned job'}
                </button>
                <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>{fmtWhen(r.created_at)}</span>
              </div>

              {/* Note text */}
              <div style={{ fontSize: 14, color: '#1f2937', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{r.note}</div>

              {/* Media thumbnails */}
              {Array.isArray(r.media) && r.media.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {r.media.map((m, i) => (
                    <img key={i} src={m} alt="" style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                  ))}
                </div>
              )}

              {/* Tags + re-tag */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f1f1' }}>
                <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>Filed under:</span>
                {tags.length === 0 && <span style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>untagged</span>}
                {tags.map(t => {
                  const c = TAG_COLOR[t] || TAG_COLOR['General'];
                  return <span key={t} style={{ background: c.bg, color: c.color, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20 }}>{t}</span>;
                })}
                {!editing && (
                  <button onClick={() => setEditId(r.id)} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #e87722', color: '#e87722', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, cursor: 'pointer' }}>
                    Re-file
                  </button>
                )}
              </div>

              {/* Re-tag picker */}
              {editing && (
                <div style={{ marginTop: 10, background: '#faf7f3', border: '1px solid #f0e2d3', borderRadius: 10, padding: 10 }}>
                  <div style={{ fontSize: 11, color: '#92400e', fontWeight: 700, marginBottom: 8 }}>Move this update to:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {BUCKETS.map(b => {
                      const active = tags[0] === b;
                      return (
                        <button key={b} disabled={saving} onClick={() => retag(r.id, b)}
                          style={{ background: active ? '#e87722' : '#fff', color: active ? '#fff' : '#374151', border: '1px solid ' + (active ? '#e87722' : '#d1d5db'), fontSize: 12, fontWeight: 600, padding: '6px 11px', borderRadius: 8, cursor: saving ? 'default' : 'pointer' }}>
                          {b}
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={() => setEditId(null)} style={{ marginTop: 8, background: 'none', border: 'none', color: '#6b7280', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>Cancel</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
